/**
 * Pending Requests List Component
 * Displays and manages pending friend requests
 */

import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Avatar,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material'
import {
  People as PeopleIcon,
  Check as AcceptIcon,
  Block as BlockIcon,
  Close as DeclineIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { UserFacingFriendStatus } from '@api/types/social'
import type { FriendDto } from '@api/types/social'
import { formatRelativeTime } from '@utils/dateUtils'

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #f093fb, #f5576c)',
  'linear-gradient(135deg, #4facfe, #00f2fe)',
  'linear-gradient(135deg, #43e97b, #38f9d7)',
  'linear-gradient(135deg, #fa709a, #fee140)',
  'linear-gradient(135deg, #a18cd1, #fbc2eb)',
  'linear-gradient(135deg, #fccb90, #d57eeb)',
]

function getAvatarGradient(name: string): string {
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]
}

interface PendingRequestsListProps {
  filterText?: string
}

export function PendingRequestsList({ filterText = '' }: PendingRequestsListProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch all friends data
  const { data: friendsData, isLoading, error } = useQuery({
    queryKey: ['friends'],
    queryFn: socialApi.getFriends,
  })

  // Filter for pending requests only
  const allPendingRequests = friendsData?.filter(f =>
    f.userFacingStatus === UserFacingFriendStatus.Pending
  ) || []

  // Apply text filter
  const pendingRequests = allPendingRequests.filter(request => {
    if (!filterText) return true

    const isRequester = request.requestedByCurrentUser
    const displayName = request.displayName || (isRequester ? request.receiverName : request.requesterName) || ''
    const username = request.username || ''
    const query = filterText.toLowerCase()

    return displayName.toLowerCase().includes(query) || username.toLowerCase().includes(query)
  })

  // Accept friend request mutation
  const acceptMutation = useMutation({
    mutationFn: (requestId: string) => socialApi.acceptFriendRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friends'] })
    },
  })

  // Reject/Cancel friend request mutation
  const rejectMutation = useMutation({
    mutationFn: (requestId: string) => socialApi.rejectFriendRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friends'] })
    },
  })

  // Block user mutation
  const blockMutation = useMutation({
    mutationFn: (userId: string) => socialApi.blockUser({ userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friends'] })
    },
  })

  const handleAccept = (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation()
    acceptMutation.mutate(requestId)
  }

  const handleReject = (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation()
    rejectMutation.mutate(requestId)
  }

  const handleBlock = (e: React.MouseEvent, request: FriendDto) => {
    e.stopPropagation()
    const userId = request.requestedByCurrentUser
      ? request.receiverUserId
      : request.requesterUserId
    const displayName = request.displayName || (request.requestedByCurrentUser ? request.receiverName : request.requesterName) || 'this user'

    if (confirm(`Are you sure you want to block ${displayName}?`)) {
      blockMutation.mutate(userId)
    }
  }

  const handleProfileClick = (userId: string) => {
    navigate(`/profile/${userId}`)
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to load pending requests'}
      </Alert>
    )
  }

  if (!pendingRequests || pendingRequests.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, opacity: 0.5 }}>
        <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No Pending Requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          When you send or receive friend requests, they'll appear here.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {pendingRequests.map((request) => {
        const isRequester = request.requestedByCurrentUser
        const displayName = request.displayName || (isRequester ? request.receiverName : request.requesterName) || 'Unknown User'
        const avatar = request.avatar
        const userId = isRequester ? request.receiverUserId : request.requesterUserId
        const timeAgo = request.requestDate ? formatRelativeTime(request.requestDate) : ''

        return (
          <Box
            key={request.id}
            onClick={() => handleProfileClick(userId)}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '14px',
              p: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
              cursor: 'pointer',
            }}
          >
            {/* Avatar */}
            <Avatar
              src={avatar}
              alt={displayName}
              sx={{
                width: 48,
                height: 48,
                background: avatar ? undefined : getAvatarGradient(displayName),
                fontSize: 18,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                {isRequester ? 'You sent a request' : 'Sent request'} {timeAgo}
              </Typography>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
              {!isRequester ? (
                <>
                  <Tooltip title="Accept">
                    <Button
                      size="small"
                      onClick={(e) => handleAccept(e, request.id)}
                      disabled={acceptMutation.isPending}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '10px',
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: 'rgba(72,187,120,0.12)',
                        border: '1px solid rgba(72,187,120,0.25)',
                        color: 'success.main',
                        '&:hover': { bgcolor: 'rgba(72,187,120,0.2)' },
                      }}
                    >
                      <AcceptIcon sx={{ fontSize: 16 }} />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Decline">
                    <Button
                      size="small"
                      onClick={(e) => handleReject(e, request.id)}
                      disabled={rejectMutation.isPending}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '10px',
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: 'rgba(252,129,129,0.08)',
                        border: '1px solid rgba(252,129,129,0.15)',
                        color: 'error.main',
                        '&:hover': { bgcolor: 'rgba(252,129,129,0.15)' },
                      }}
                    >
                      <DeclineIcon sx={{ fontSize: 16 }} />
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Cancel Request">
                  <Button
                    size="small"
                    onClick={(e) => handleReject(e, request.id)}
                    disabled={rejectMutation.isPending}
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '10px',
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: 'rgba(252,129,129,0.08)',
                      border: '1px solid rgba(252,129,129,0.15)',
                      color: 'error.main',
                      '&:hover': { bgcolor: 'rgba(252,129,129,0.15)' },
                    }}
                  >
                    Cancel
                  </Button>
                </Tooltip>
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
